// frontend/src/pages/Documents.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { trackDocumentDownload } from '../utils/analytics';
import { ArticleIcon as Doc, AddIcon as Add, DeleteIcon as Delete, CloseIcon as X, DownloadIcon as Download, EditIcon as Edit } from '../components/Icons';

const Documents = () => {
    const { API_URL, user } = useContext(AuthContext);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showModal, setShowModal] = useState(false);
    const [filterType, setFilterType] = useState('all');

    const [formData, setFormData] = useState({ name: '', type: 'general', description: '', fileUrl: '', visibility: 'members', requiresSignature: false });
    const typeOptions = [{ value: 'general', label: 'Allgemein' }, { value: 'consent', label: 'Einwilligung' }, { value: 'medical', label: 'Medizinisch' }, { value: 'financial', label: 'Finanziell' }];

    useEffect(() => { fetchDocuments(); }, [filterType]);

    const fetchDocuments = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const url = filterType === 'all' ? `${API_URL}/documents` : `${API_URL}/documents?type=${filterType}`;
            const { data } = await axios.get(url, config);
            setDocuments(data);
            setLoading(false);
        } catch (error) { setMessage({ type: 'error', text: 'Fehler beim Laden' }); setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.post(`${API_URL}/documents`, formData, config);
            setMessage({ type: 'success', text: 'Dokument erstellt!' });
            setShowModal(false);
            setFormData({ name: '', type: 'general', description: '', fileUrl: '', visibility: 'members', requiresSignature: false });
            fetchDocuments();
        } catch (error) { setMessage({ type: 'error', text: 'Fehler beim Erstellen' }); }
    };

    const deleteDocument = async (id) => {
        if (!window.confirm('Dokument löschen?')) return;
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.delete(`${API_URL}/documents/${id}`, config);
            setMessage({ type: 'success', text: 'Gelöscht!' });
            fetchDocuments();
        } catch (error) { setMessage({ type: 'error', text: 'Fehler' }); }
    };

    if (loading) return <div className="flex justify-center items-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>;

    return (
        <div className="container mx-auto p-4 max-w-7xl animate-fade-in">
            <div className="mb-8 flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary-container text-primary-onContainer flex items-center justify-center"><Doc style={{ fontSize: 28 }} /></div>
                    <div><h1 className="text-display-small font-bold">Dokumente</h1><p className="text-body-medium text-surface-onVariant">{documents.length} Dokumente</p></div>
                </div>
                <button onClick={() => setShowModal(true)} className="md3-btn-filled flex items-center gap-2"><Add style={{ fontSize: 20 }} />Hochladen</button>
            </div>

            {message.text && <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${message.type === 'error' ? 'bg-error-container' : 'bg-primary-container'}`}><span>{message.text}</span><button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto"><X style={{ fontSize: 18 }} /></button></div>}

            <div className="flex gap-2 mb-6">
                {['all', ...typeOptions.map(t => t.value)].map(t => (
                    <button key={t} onClick={() => setFilterType(t)} className={`px-4 py-2 rounded-full text-sm font-medium ${filterType === t ? 'bg-primary text-white' : 'bg-surface-variant text-surface-onVariant'}`}>
                        {t === 'all' ? 'Alle' : typeOptions.find(o => o.value === t)?.label}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map(doc => (
                    <div key={doc._id} className="md3-card-elevated p-4">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <h3 className="font-bold text-title-medium">{doc.name}</h3>
                                <span className="text-label-small text-surface-onVariant">{typeOptions.find(t => t.value === doc.type)?.label}</span>
                            </div>
                            <span className="px-2 py-0.5 rounded text-label-small bg-surface-variant">v{doc.version}</span>
                        </div>
                        {doc.description && <p className="text-body-small text-surface-onVariant mb-3 line-clamp-2">{doc.description}</p>}
                        {doc.requiresSignature && <p className="text-label-small text-tertiary mb-2">✍️ {doc.signatures?.length || 0} Unterschriften</p>}
                        <div className="flex gap-2 pt-3 border-t border-outlineVariant">
                            {doc.fileUrl && <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="md3-btn-tonal flex-1 text-center" onClick={() => trackDocumentDownload(doc.name)}><Download style={{ fontSize: 16 }} className="mr-1" />Download</a>}
                            <button onClick={() => deleteDocument(doc._id)} className="md3-icon-btn text-error"><Delete style={{ fontSize: 18 }} /></button>
                        </div>
                    </div>
                ))}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="md3-card-elevated max-w-lg w-full p-8">
                        <h2 className="text-title-large font-bold mb-6">Neues Dokument</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div><label className="block text-label-large mb-2">Name *</label><input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="md3-textfield-outlined" required /></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="block text-label-large mb-2">Typ</label><select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="md3-textfield-outlined">{typeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
                                <div><label className="block text-label-large mb-2">Sichtbarkeit</label><select value={formData.visibility} onChange={(e) => setFormData({ ...formData, visibility: e.target.value })} className="md3-textfield-outlined"><option value="public">Öffentlich</option><option value="members">Mitglieder</option><option value="leaders">Leiter</option><option value="admin">Admin</option></select></div>
                            </div>
                            <div><label className="block text-label-large mb-2">Datei-URL</label><input type="url" value={formData.fileUrl} onChange={(e) => setFormData({ ...formData, fileUrl: e.target.value })} className="md3-textfield-outlined" placeholder="https://..." /></div>
                            <div><label className="block text-label-large mb-2">Beschreibung</label><textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="md3-textfield-outlined" /></div>
                            <label className="flex items-center gap-3 cursor-pointer"><input type="checkbox" checked={formData.requiresSignature} onChange={(e) => setFormData({ ...formData, requiresSignature: e.target.checked })} className="accent-primary w-5 h-5" /><span>Erfordert Unterschrift</span></label>
                            <div className="flex gap-3 pt-4"><button type="submit" className="md3-btn-filled flex-1">Erstellen</button><button type="button" onClick={() => setShowModal(false)} className="md3-btn-text flex-1">Abbrechen</button></div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Documents;
